import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Alert, Chip, MenuItem,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import api from "../services/api";

const price = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

export default function PriceAlerts() {
  const [threshold, setThreshold] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    setLoading(true); setError(null);
    api.get("/prices/movers", { params: { threshold } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.message || "Failed to load price movers"))
      .finally(() => setLoading(false));
  }, [threshold]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Price Alerts &mdash; Big Movers</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Materials whose best market price moved sharply between their two most recent price dates. Use this
        to <strong>adjust intake pricing the same day</strong> a metal jumps or drops. Read-only — reads your
        existing daily price feed, changes nothing.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField select size="small" label="Alert threshold" value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))} sx={{ minWidth: 160 }}>
            {[1, 2, 3, 5, 10].map((t) => <MenuItem key={t} value={t}>&plusmn; {t}% or more</MenuItem>)}
          </TextField>
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Refresh"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          {data.movers.length === 0 ? (
            <Alert severity="success">
              No material moved by {data.threshold}% or more between its last two price dates. Prices are steady.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                {data.count} material(s) moved &plusmn;{data.threshold}% or more.
              </Typography>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Prior</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Latest</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Change</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.movers.map((m, i) => {
                      const up = m.direction === "up";
                      const color = up ? "success.main" : "error.main";
                      return (
                        <TableRow key={`${m.canonical}-${i}`}>
                          <TableCell sx={{ py: 0.5 }}>
                            {m.canonical}
                            <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                              {m.priorDate} &rarr; {m.latestDate}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Chip size="small" label={m.category || "—"} variant="outlined" />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontVariantNumeric: "tabular-nums" }}>{price(m.priorPrice)}</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{price(m.latestPrice)}</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontVariantNumeric: "tabular-nums", color }}>
                            {up ? "+" : "−"}{price(Math.abs(m.change))}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontVariantNumeric: "tabular-nums", fontWeight: 800, color }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                              {up ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                              <span>{up ? "+" : ""}{m.changePct}%</span>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
                "Best" price is the highest across vendors on each date (you're the seller). Change compares each
                material's two most recent price dates within the last {data.lookback} days.
              </Typography>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
